import { Box, Tooltip, Typography, Divider } from '@mui/material';
import { ReactComponent as AutoGeneratedIcon } from 'assets/icons/auto-generated.svg';
import { styled } from '@mui/material/styles';
import DraftStatusMessage from './DraftStatusMessage';
import { ArticleType } from '../hooks/useArticlesQuery';
import { ArticleUserMapType } from 'pages/article/hooks/useArticleDetailsQuery';
import { Dispatch, useMemo } from 'react';
import useUserData from 'hooks/useUserData';
import { getDraftDetails } from 'utility/helpers';
import { ArticleVariantType } from './ArticleBox';
import { UseArticleReducerActions } from '../hooks/useArticle';

type AutoGeneratedDraftMessageProps = {
  draft: ArticleType;
  userMap: ArticleUserMapType;
  isAutoGeneratedDraft: boolean;
  variant: ArticleVariantType;
  dispatch: Dispatch<UseArticleReducerActions>;
};

function AutoGeneratedDraftMessage({
  variant,
  draft,
  userMap,
  isAutoGeneratedDraft,
  dispatch,
}: AutoGeneratedDraftMessageProps) {
  const { user } = useUserData();
  const draftMessageData = useMemo(() => getDraftDetails(draft, user, userMap), [draft]);

  const hasMessageToDisplay = useMemo(() => {
    const { isAssignedToMe, assignedBy } = draftMessageData;

    return isAutoGeneratedDraft || (isAssignedToMe && assignedBy);
  }, [draftMessageData, isAutoGeneratedDraft]);

  return (
    <>
      <$Container
        justifyContent={isAutoGeneratedDraft ? 'space-between' : 'flex-end'}
        display={hasMessageToDisplay ? 'flex' : 'none'}
        className={variant}
      >
        {isAutoGeneratedDraft && (
          <Tooltip
            placement="top"
            componentsProps={{
              tooltip: { sx: { padding: 1, maxWidth: 'fit-content', mb: '4px !important' } },
            }}
            title={
              <Typography>
                This draft has been automatically generated by BuildWise AI. &nbsp;
                {/* <$LearnMore>Learn More</$LearnMore> */}
              </Typography>
            }
          >
            <$Message variant="caption">
              <AutoGeneratedIcon width={20} height={20} /> &nbsp;&nbsp;AI-generated draft
            </$Message>
          </Tooltip>
        )}
        <DraftStatusMessage {...draftMessageData} dispatch={dispatch} />
      </$Container>
      {hasMessageToDisplay && <$Divider />}
    </>
  );
}

const $Container = styled(Box, { label: 'Container' })({
  padding: '8px 16px',
  width: '100%',
  alignItems: 'center',
  '&.detailed': {
    padding: '8px 24px',
  },
});

const $Message = styled(Typography, { label: 'Message' })(({ theme }) => ({
  width: 'fit-content',
  color: theme.palette.custom.link,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  padding: '4px 8px',
  backgroundColor: theme.palette.custom.lilyWhite,
  borderRadius: 4,
}));

const $Divider = styled(Divider, { label: 'Divider' })(({ theme }) => ({
  borderColor: theme.palette.custom.blueChalk,
}));

// const $LearnMore = styled(Typography, { label: 'LearnMore' })({
//   textDecoration: 'underline',
//   display: 'inline',
//   cursor: 'pointer',
//   '&:hover': { fontWeight: 'bold', transition: 'font-weight 0.3s' },
// });

export default AutoGeneratedDraftMessage;
